<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { getMantenimiento } from '@/components/utils/functions'
import { IMantenimiento } from '@shared/types/base'
import { ETipoMantenimientoType, NOMBRE_TIPO_MANTENIMIENTO } from '@shared/types/enums'
import { apiService } from '@/services/apiService'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import BaseModal from '../BaseModal.vue'

const loading = ref(false)
const error = ref<string>('')
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref<string>('')

const DEFAULT_MANTENIMIENTO: IMantenimiento = {
	mensaje: '',
	titulo: '',
	tipo: ETipoMantenimientoType.INFO,
	fechaInicio: Date.now(),
	fechaFin: Date.now()
}

// Importante: no puede ser `null` porque el template se renderiza antes de `onMounted`
// y el `v-model` intentará acceder a propiedades como `titulo`.
const mantenimiento = ref<IMantenimiento>({ ...DEFAULT_MANTENIMIENTO })

const obtenerMantenimiento = async (): Promise<void> => {
	const mantenimientoData = await getMantenimiento()
	if (mantenimientoData) {
		mantenimiento.value = {
			...DEFAULT_MANTENIMIENTO,
			...mantenimientoData,
			// Normalización defensiva por si el backend devuelve null/undefined
			mensaje: mantenimientoData.mensaje ?? '',
			titulo: mantenimientoData.titulo ?? '',
			tipo: mantenimientoData.tipo ?? DEFAULT_MANTENIMIENTO.tipo,
			fechaInicio: mantenimientoData.fechaInicio ?? DEFAULT_MANTENIMIENTO.fechaInicio,
			fechaFin: mantenimientoData.fechaFin ?? DEFAULT_MANTENIMIENTO.fechaFin
		}
	} else {
		mantenimiento.value = { ...DEFAULT_MANTENIMIENTO }
	}
}
// Cargar transacciones cuando el componente se monte
onMounted(async () => {
	await obtenerMantenimiento()
})

const saveMantenimiento = async () => {
	await apiService
		.post('createMaintenanceSale/crear', {
			MaintenanceSale: {
				titulo: mantenimiento.value.titulo,
				mensaje: mantenimiento.value.mensaje,
				tipo: mantenimiento.value.tipo,
				fechaInicio: mantenimiento.value.tipo === ETipoMantenimientoType.MAINTENANCE ? new Date(mantenimiento.value.fechaInicio!).getTime() : null,
				fechaFin: mantenimiento.value.tipo === ETipoMantenimientoType.MAINTENANCE ? new Date(mantenimiento.value.fechaFin!).getTime() : null
			}
		})
		.catch((error: any) => {
			console.error('Error al guardar mantenimiento:', error)
			error.value = error.message || 'Error al guardar el mantenimiento. Verifica la consola para más detalles.'
			showErrorModal.value = true
		})
		.then(() => {
			successMessage.value = 'Mantenimiento guardado correctamente'
			showSuccessModal.value = true
			loading.value = false
		})
}
const deleteMantenimiento = async () => {
	await apiService
		.post('createMaintenanceSale/eliminar')
		.catch((error: any) => {
			console.error('Error al eliminar mantenimiento:', error)
			error.value = error.message || 'Error al eliminar el mantenimiento. Verifica la consola para más detalles.'
			showErrorModal.value = true
		})
		.then(() => {
			successMessage.value = 'Mantenimiento eliminado correctamente'
			showSuccessModal.value = true
			loading.value = false
		})
}
</script>
<template>
	<va-card class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="account_balance" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">Mantenimiento</h2>
					</div>
				</div>
			</div>
		</va-card-title>

		<va-card-content class="card-content">
			<div class="section-title" style="margin-top: 1rem">
				<div class="section-title-content">
					<div class="row">
						<div class="col-md-10">
							<va-icon name="description" class="title-icon" />
							<span style="margin-left: 0.5rem">Configuración</span>
						</div>
					</div>
					<div class="col-md-2">
						<va-button @click="saveMantenimiento()" color="success" style="margin-right: 0.5rem">
							<va-icon name="save" class="button-icon" />
							Guardar
						</va-button>
						<va-button @click="deleteMantenimiento()" color="danger">
							<va-icon name="cancel" class="button-icon" />
							Eliminar
						</va-button>
					</div>
				</div>
			</div>
			<div class="collapse-body">
				<div class="collapse-content">
					<div class="mantenimiento-form">
						<div class="form-grid">
							<div class="form-field">
								<label for="mantenimientoTitulo" class="form-label">
									<va-icon name="pin" class="label-icon" />
									Título
								</label>
								<va-input
									id="mantenimientoTitulo"
									v-model="mantenimiento.titulo"
									placeholder="Ingrese el título de la venta de mantenimiento"
									class="form-input"
									style="width: 100%; margin-top: 0.2rem"
									:rules="[value => !!value || 'El título de la venta de mantenimiento es requerido']" />
							</div>

							<div class="form-field">
								<label for="mantenimientoTipo" class="form-label">
									<va-icon name="category" class="label-icon" />
									Tipo de mantenimiento
								</label>
								<select id="mantenimientoTipo" v-model="mantenimiento.tipo" class="form-select form-input">
									<option :value="null" disabled>Seleccione un tipo de mantenimiento...</option>
									<option :value="ETipoMantenimientoType.INFO">{{ NOMBRE_TIPO_MANTENIMIENTO[ETipoMantenimientoType.INFO] }}</option>
									<option :value="ETipoMantenimientoType.WARNING">{{ NOMBRE_TIPO_MANTENIMIENTO[ETipoMantenimientoType.WARNING] }}</option>
									<option :value="ETipoMantenimientoType.ERROR">{{ NOMBRE_TIPO_MANTENIMIENTO[ETipoMantenimientoType.ERROR] }}</option>
									<option :value="ETipoMantenimientoType.MAINTENANCE">{{ NOMBRE_TIPO_MANTENIMIENTO[ETipoMantenimientoType.MAINTENANCE] }}</option>
								</select>
							</div>
							<div class="form-field">
								<div class="form-label">
									<va-icon name="event" class="label-icon" />
									Fecha de inicio
								</div>
								<VueDatePicker v-model="mantenimiento.fechaInicio" :formats="{ input: 'dd/MM/yyyy HH:mm' }" />
							</div>

							<div class="form-field">
								<div class="form-label">
									<va-icon name="event_available" class="label-icon" />
									Fecha de fin
								</div>
								<VueDatePicker v-model="mantenimiento.fechaFin" :formats="{ input: 'dd/MM/yyyy HH:mm' }" />
							</div>

							<div class="form-field form-field--full">
								<label for="mantenimientoMensaje" class="form-label">
									<va-icon name="chat" class="label-icon" />
									Mensaje de la venta de mantenimiento
								</label>
								<va-textarea
									id="mantenimientoMensaje"
									v-model="mantenimiento.mensaje"
									placeholder="Ingrese el mensaje de la venta de mantenimiento"
									class="form-input"
									min-rows="15"
									autosize
									:rules="[value => !!value || 'El mensaje de la venta de mantenimiento es requerido']" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</va-card-content>
	</va-card>
	<BaseModal v-model="showErrorModal" title="Error" :message="error" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Header mejorado */
.header-gradient {
	background: #062342;
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.header-icon-wrapper {
	width: 34px;
	height: 34px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.form-label {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-weight: 600;
	color: #1a202c;
	font-size: 0.875rem;
	margin-bottom: 0.25rem;
}

.label-icon {
	color: #062342;
	font-size: 1rem;
}

.form-input {
	width: 100%;
}

.button-icon {
	margin-right: 0.5rem;
}

:deep(.scrollable-table .va-data-table) {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table__table-wrapper) {
	overflow: visible !important;
	height: auto !important;
}

:deep(.scrollable-table table) {
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table) {
	background: transparent;
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table thead) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	z-index: 10;
}

:deep(.scrollable-table .va-data-table__table thead th) {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
}

:deep(.scrollable-table .va-data-table__table tbody tr) {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

:deep(.scrollable-table .va-data-table__table tbody tr:hover) {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

:deep(.scrollable-table .va-data-table__table tbody td) {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	:deep(.scrollable-table .va-data-table__table thead th),
	:deep(.scrollable-table .va-data-table__table tbody td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.8125rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}
}

.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 1.5rem;
	margin-bottom: 2rem;
}

.form-field--full {
	grid-column: 1 / -1;
}

.form-select {
	padding: 0.625rem 0.75rem;
	border-radius: 8px;
	border: 1px solid var(--va-background-border, #e2e8f0);
	background: var(--va-background-element, #ffffff);
	color: var(--va-text-primary, #1a202c);
	outline: none;
}

.form-select:focus {
	border-color: var(--va-primary, #062342);
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Responsive del formulario */
@media (max-width: 768px) {
	.form-grid {
		grid-template-columns: 1fr;
		gap: 1.25rem;
	}
}

/* Secciones */
.section-title {
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 3px solid transparent;
	border-image: #062342 1;
	animation: slideInLeft 0.5s ease-out;
}

.section-title-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a202c;
}

.title-icon {
	color: #062342;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.section-title-content {
		font-size: 1.25rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

/* Responsive del formulario */
@media (max-width: 768px) {
	.form-grid {
		grid-template-columns: 1fr;
		gap: 1.25rem;
	}
}

.collapse-body {
	border: 2px solid var(--va-background-border);
	border-top: none;
	border-radius: 0 0 12px 12px;
	background: var(--va-background-element);
}

.collapse-content {
	padding: 1.25rem;
	color: var(--va-text-secondary, #4a5568);
	line-height: 1.6;
}
</style>
